import React, { useState, useEffect } from 'react';
import {
  Container,
  Box,
  Typography,
  TextField,
  Button,
  Paper,
  Alert,
  CircularProgress
} from '@mui/material';
import { signInWithEmailAndPassword } from 'firebase/auth';
import { auth } from '../firebase';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { preloadCoreData } from '../services';

/**
 * Login Page - Simplified secure implementation
 *
 * SECURITY: No sensitive data stored in localStorage.
 * Firebase Auth handles token persistence securely.
 * User profile is fetched by AuthContext via onAuthStateChanged.
 */
const Login = () => {
  const { user, sessionExpired, clearSessionExpired, authError, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // Navigate to dashboard when user is authenticated
  useEffect(() => {
    if (user && !authLoading) {
      // User is authenticated - preload data and navigate
      const completeLogin = async () => {
        try {
          await preloadCoreData(user.adminCompanyIds);
        } catch (preloadError) {
          // Don't block navigation if preload fails
        }
        navigate('/');
      };
      completeLogin();
    }
  }, [user, authLoading, navigate]);

  // Show auth errors from AuthContext
  useEffect(() => {
    if (authError === 'no_admin_access') {
      setError('Sie haben keine Berechtigung für dieses Portal. (31)');
      setLoading(false);
    } else if (authError === 'profile_fetch_error') {
      setError('Fehler beim Laden des Profils. Bitte versuchen Sie es erneut.');
      setLoading(false);
    }
  }, [authError]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      // Authenticate with Firebase - AuthContext will handle the rest
      await signInWithEmailAndPassword(auth, email, password);

      // Clear any previous session expired flag
      clearSessionExpired();

      // AuthContext's onAuthStateChanged will:
      // 1. Detect the login
      // 2. Fetch the user profile from Firestore
      // 3. Set the user in context
      // 4. The useEffect above will then navigate to dashboard

    } catch (err) {
      setLoading(false);

      // Handle Firebase Auth errors
      if (err.code === 'auth/user-not-found' || err.code === 'auth/wrong-password' || err.code === 'auth/invalid-credential') {
        setError('Anmeldedaten nicht korrekt. (13)');
      } else if (err.code === 'auth/too-many-requests') {
        setError('Zu viele fehlgeschlagene Anmeldeversuche. Bitte versuchen Sie es später erneut.');
      } else if (err.code === 'auth/invalid-email') {
        setError('Ungültige E-Mail-Adresse.');
      } else {
        setError('Login fehlgeschlagen. Bitte versuche es erneut.');
      }
    }
  };

  return (
    <Container component="main" maxWidth="xs">
      <Box
        sx={{
          marginTop: 8,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <Paper
          elevation={3}
          sx={{
            p: 4,
            width: '100%',
            borderRadius: 2,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}
        >
          <Typography component="h1" variant="h4" sx={{ mb: 3 }}>
            kohara Admin
          </Typography>

          {sessionExpired && (
            <Alert severity="warning" sx={{ width: '100%', mb: 2 }}>
              Ihre Sitzung ist abgelaufen. Bitte melden Sie sich erneut an.
            </Alert>
          )}

          {error && (
            <Alert severity="error" sx={{ width: '100%', mb: 2 }}>
              {error}
            </Alert>
          )}

          <Box component="form" onSubmit={handleLogin} sx={{ mt: 1, width: '100%' }}>
            <TextField
              margin="normal"
              required
              fullWidth
              id="email"
              label="E-Mail"
              name="email"
              autoComplete="email"
              autoFocus
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
            <TextField
              margin="normal"
              required
              fullWidth
              name="password"
              label="Passwort"
              type="password"
              id="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
            <Button
              type="submit"
              fullWidth
              variant="contained"
              sx={{ mt: 3, mb: 2, py: 1.5 }}
              disabled={loading || authLoading}
            >
              {(loading || authLoading) ? <CircularProgress size={24} /> : 'Anmelden'}
            </Button>
          </Box>
        </Paper>
      </Box>
      <Box sx={{ mt: 8, textAlign: 'center' }}>
        <Typography variant="body2" color="text.secondary">
          © {new Date().getFullYear()} SFT-Solutions
        </Typography>
      </Box>
    </Container>
  );
};

export default Login;
